import { Observation } from "@medplum/fhirtypes";

import type {
  LaborMedTherapyManeuverRow,
  LaborMedTherapyMedicationRow,
  LaborMedTherapyTherapyRow,
} from "@/encounters/components/EncounterLabor/EncounterLaborVersionDiff/LaborObservationMedTherapyDiffColumns";
import {
  getStructuredMedTherapyConfig,
  normalizeObservationFormCode,
} from "@/encounters/components/EncounterLabor/EncounterLaborVersionDiff/structuredMedTherapyDiffConfig";
import { laborObservationDisplayValuesDiffer } from "@/encounters/components/EncounterLabor/utils/labor-observation-amend-diff-display";

import { getLaborObservationDisplayValuesForVersionDiff } from "./labor-observation-version-diff-values";

/**
 * Returns whether two structured med/therapy row lists differ by serialized content.
 */
function structuredMedTherapyRowsDiffer<T>(current?: T[], previous?: T[]): boolean {
  return JSON.stringify(current ?? []) !== JSON.stringify(previous ?? []);
}

/**
 * Returns whether a labor/postpartum flowsheet row should appear in version diff UI.
 *
 * @param currentObservation - Amended observation row, if present.
 * @param previousObservation - Pre-amend predecessor row, if present.
 */
export function laborObservationVersionDiffRowHasVisibleChange(
  currentObservation?: Observation,
  previousObservation?: Observation,
): boolean {
  if (!currentObservation && !previousObservation) {
    return false;
  }

  if (!currentObservation || !previousObservation) {
    return true;
  }

  const currentValues = getLaborObservationDisplayValuesForVersionDiff(currentObservation);
  const previousValues = getLaborObservationDisplayValuesForVersionDiff(previousObservation);

  const codeForForm =
    currentObservation.code?.coding?.[0]?.code ?? previousObservation.code?.coding?.[0]?.code;
  const formNameForStructured = normalizeObservationFormCode(codeForForm);
  const {
    usesStructuredDiff: usesStructuredMedTherapyDiff,
    includeManeuvers: includeManeuversInStructuredDiff,
  } = getStructuredMedTherapyConfig(formNameForStructured);

  const structuredExcludeFields = usesStructuredMedTherapyDiff
    ? ["medications", "therapies", ...(includeManeuversInStructuredDiff ? ["maneuvers"] : [])]
    : [];

  const hasPlainFieldDiff = laborObservationDisplayValuesDiffer(currentValues, previousValues, {
    structuredExcludeFields,
  });

  if (!usesStructuredMedTherapyDiff) {
    return hasPlainFieldDiff;
  }

  const structuredMedications = (currentValues as { medications?: LaborMedTherapyMedicationRow[] })
    .medications;
  const structuredPreviousMedications = (
    previousValues as { medications?: LaborMedTherapyMedicationRow[] }
  ).medications;
  const structuredTherapies = (currentValues as { therapies?: LaborMedTherapyTherapyRow[] })
    .therapies;
  const structuredPreviousTherapies = (
    previousValues as { therapies?: LaborMedTherapyTherapyRow[] }
  ).therapies;
  const structuredManeuvers = (currentValues as { maneuvers?: LaborMedTherapyManeuverRow[] })
    .maneuvers;
  const structuredPreviousManeuvers = (
    previousValues as { maneuvers?: LaborMedTherapyManeuverRow[] }
  ).maneuvers;

  const hasStructuredMedTherapyDiff =
    structuredMedTherapyRowsDiffer(structuredMedications, structuredPreviousMedications) ||
    structuredMedTherapyRowsDiffer(structuredTherapies, structuredPreviousTherapies) ||
    (includeManeuversInStructuredDiff &&
      structuredMedTherapyRowsDiffer(structuredManeuvers, structuredPreviousManeuvers));

  return hasStructuredMedTherapyDiff || hasPlainFieldDiff;
}
